import { useEffect, useState } from "react";
import { api, formatNaira, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, GripVertical, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadCsv } from "@/lib/exports";

const STAGES = [
  { key: "new", label: "New", color: "bg-blue-500" },
  { key: "contacted", label: "Contacted", color: "bg-violet-500" },
  { key: "qualified", label: "Qualified", color: "bg-brand-aqua" },
  { key: "proposal", label: "Proposal", color: "bg-amber-500" },
  { key: "won", label: "Won", color: "bg-emerald-500" },
  { key: "lost", label: "Lost", color: "bg-red-400" },
];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ contact_id: "", title: "", value: 0, business_unit: "FigureWeb", stage: "new", expected_close: "" });
  const [dragId, setDragId] = useState(null);

  const load = () => api.get("/leads").then(r => setLeads(r.data));
  useEffect(() => {
    load();
    api.get("/contacts").then(r => setContacts(r.data));
  }, []);

  const create = async () => {
    if (!form.title) return toast.error("Title required");
    const selected = contacts.find(c => c.id === form.contact_id);
    const payload = {
      ...form,
      value: Number(form.value) || 0,
      contact_name: selected?.name || "",
      company: selected?.company || "",
    };
    await api.post("/leads", payload);
    toast.success("Lead created");
    setOpen(false);
    setForm({ contact_id: "", title: "", value: 0, business_unit: "FigureWeb", stage: "new", expected_close: "" });
    load();
  };

  const moveTo = async (leadId, newStage) => {
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, stage: newStage } : l));
    try {
      await api.patch(`/leads/${leadId}`, { stage: newStage });
      toast.success(`Moved to ${newStage}`);
    } catch { toast.error("Failed to move"); load(); }
  };

  const totalByStage = (key) => leads.filter(l => l.stage === key).reduce((s, l) => s + Number(l.value || 0), 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leads Pipeline"
        subtitle="Drag leads between stages to progress deals"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => downloadCsv("leads")} data-testid="export-leads-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-lead-btn" className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">
                <Plus className="w-4 h-4 mr-1" /> New Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader><DialogTitle>New Lead</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title / Opportunity</Label>
                  <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="lead-title-input" /></div>
                <div><Label>Contact</Label>
                  <Select value={form.contact_id} onValueChange={v => setForm({...form, contact_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select contact" /></SelectTrigger>
                    <SelectContent>
                      {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name} — {c.company}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Value (₦)</Label>
                    <Input type="number" value={form.value} onChange={e => setForm({...form, value: e.target.value})} /></div>
                  <div><Label>Expected close</Label>
                    <Input type="date" value={form.expected_close} onChange={e => setForm({...form, expected_close: e.target.value})} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Business Unit</Label>
                    <Select value={form.business_unit} onValueChange={v => setForm({...form, business_unit: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FigureWeb">FigureWeb</SelectItem>
                        <SelectItem value="FigureCX">FigureCX</SelectItem>
                        <SelectItem value="Telecom">Telecom</SelectItem>
                        <SelectItem value="FigureAcademy">Figure Academy</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div><Label>Stage</Label>
                    <Select value={form.stage} onValueChange={v => setForm({...form, stage: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={create} data-testid="save-lead-btn" className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="overflow-x-auto pb-4">
        <div className="grid grid-flow-col auto-cols-[300px] gap-4">
          {STAGES.map(stage => {
            const items = leads.filter(l => l.stage === stage.key);
            return (
              <div key={stage.key}
                   className="bg-slate-100/70 rounded-lg p-3 flex flex-col gap-3 min-h-[400px]"
                   data-testid={`lead-column-${stage.key}`}
                   onDragOver={(e) => e.preventDefault()}
                   onDrop={() => { if (dragId) { moveTo(dragId, stage.key); setDragId(null); } }}>
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${stage.color}`} />
                    <span className="font-heading font-semibold text-sm text-slate-800">{stage.label}</span>
                    <span className="text-xs text-slate-400 num">({items.length})</span>
                  </div>
                  <div className="text-xs text-slate-500 num">{formatNaira(totalByStage(stage.key))}</div>
                </div>
                <div className="flex flex-col gap-2">
                  {items.map(l => (
                    <div key={l.id} draggable onDragStart={() => setDragId(l.id)}
                         className="bg-white rounded-md border border-slate-200 p-3 cursor-grab hover:border-brand-aqua hover:shadow-sm transition-all group"
                         data-testid={`lead-card-${l.id}`}>
                      <div className="flex items-start gap-2">
                        <GripVertical className="w-3.5 h-3.5 text-slate-300 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">{l.title}</div>
                          <div className="text-xs text-slate-500 truncate">{l.contact_name} · {l.company}</div>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                        <span className="text-xs font-heading font-semibold text-slate-900 num">{formatNaira(l.value)}</span>
                        <Chip variant="default">{l.business_unit}</Chip>
                      </div>
                      {l.expected_close && <div className="text-[11px] text-slate-400 mt-1">Close {formatDate(l.expected_close)}</div>}
                    </div>
                  ))}
                  {items.length === 0 && <div className="text-xs text-slate-400 text-center py-6">Drop leads here</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
